<template>
	<div :id="modalName">
		<b-modal :id="modalName" title="Edit Payment" scrollable hide-footer size="xl" @shown="onModalShown">
            <template v-slot:modal-title>
                <span>Edit Payment:</span>
            </template>

            <b-form @submit.stop.prevent="editPayment">
                <b-row class="align-items-start">
                    <b-col cols="12" md="5">
                        <b-form-group
                            id="datepicker-group-date"
                            label="Date:"
                            label-for="datepicker-date"
                            label-size="sm"
                        >
                            <b-form-datepicker
                                :key="keys.datepicker"
                                id="datepicker-date"
                                v-model="payment.date"
                                class="mb-2"
                                size="sm"
                                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                locale="en"
                            >
                            </b-form-datepicker>
                        </b-form-group>

                        <b-form-group
                            id="input-group-customer"
                            label="Customer:"
                            label-for="input-customer"
                            label-size="sm"
                        >
                            <div class="options-assign">
                                <div class="autosuggest-container">
                                    <vue-autosuggest
                                        v-model="payment.customer"
                                        :suggestions="autosuggest.customer.suggestions"
                                        @click="clickHandlerCustomer"
                                        @input="fetchResultsCustomer"
                                        @selected="onSelectedCustomer"
                                        @keydown.tab="suggestFirstCustomer($event.target.value)"
                                        :get-suggestion-value="getSuggestionValueCustomer"
                                        :input-props="{id:'autosuggest__input', placeholder:'', disabled: canChangeCustomer()}"
                                        @keyup.enter="suggestFirstCustomer($event.target.value)"
                                        @change="customerChanged(0)"
                                    >

                                        <b-container slot-scope="{suggestion}" fluid>
                                            <b-row>
                                                <b-col class="text-right">
                                                    {{ suggestion.item.name }}
                                                </b-col>
                                            </b-row>
                                        </b-container>
                                    </vue-autosuggest>
                                </div>
                            </div>
                        </b-form-group>

                        <b-form-group
                            id="input-group-amount"
                            label="Amount:"
                            label-for="input-amount"
                            label-size="sm"
                        >
                            <b-form-input
                                id="input-amount"
                                v-model="payment.amount"
                                type="text"
                                placeholder=""
                                size="sm"
                                autocomplete="off"
                                :key="keys.amount"
                            >
                            </b-form-input>
                        </b-form-group>

                        <b-form-group
                            id="select-group-payment-type"
                            label="Payment Type:"
                            label-for="select-payment-type"
                            label-size="sm"
                        >
                            <b-form-select
                                v-model="payment.paymentType"
                                id="select-payment-type"
                                size="sm"
                                :options="lists.paymentTypes"
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group
                            id="input-group-reference"
                            label="Reference #:"
                            label-for="input-reference"
                            label-size="sm"
                        >
                            <b-form-input
                                id="input-reference"
                                v-model="payment.reference"
                                type="text"
                                placeholder=""
                                size="sm"
                                autocomplete="off"
                            >
                            </b-form-input>
                        </b-form-group>

                        <b-form-group
                            id="select-group-deposit-account"
                            label="Deposit Account:"
                            label-for="select-deposit-account"
                            label-size="sm"
                        >
                            <b-form-select
                                v-model="payment.depositAccount"
                                id="select-deposit-account"
                                size="sm"
                                :options="lists.depositAccounts"
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group
                            id="input-group-notes"
                            label="Notes:"
                            label-for="input-notes"
                            label-size="sm"
                        >
                            <b-form-input
                                id="input-notes"
                                v-model="payment.notes"
                                type="text"
                                placeholder=""
                                size="sm"
                                autocomplete="off"
                            >
                            </b-form-input>
                        </b-form-group>
                        <b-form-row class="button-row">
                            <b-col cols="4">
                                <b-button type="submit" variant="primary" class="btn-sm" v-if="payment.applyEditable || !payment.applyTo">
                                    <span v-if="submitting">
                                        <b-spinner small type="grow"></b-spinner>
                                        Modifying Payment...
                                    </span>
                                    <span v-else>Modify Payment</span>
                                </b-button>
                            </b-col>
                        </b-form-row>
                    </b-col>

                    <b-col cols="12" md="7" class="outstanding-orders">
                        <b-button type="button" variant="danger" class="btn-sm" v-if="!payment.applyEditable && transactions.length" @click="resetApplied">Reset Applied</b-button>
                        <span v-else-if="payment.applyEditable"><strong>Available To Apply: </strong>${{ available }}</span>
                        <h6>Balances</h6>
                        <div class="table-responsive">
                        <b-table-simple hover small bordered :key="keys.outstanding">
                            <b-thead>
                                <tr>
                                    <th></th>
                                    <th>Ref</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Remaining</th>
                                    <th>Payment Applied</th>
                                </tr>
                            </b-thead>
                            <b-tbody>
                                <tr v-for="(order, index) in transactions" :key="index">
                                    <td>
                                        <b-form-checkbox :id="'checkbox-order-' + index" :name="'checkbox-order-' + index" :value="order.id" v-model="payment.applyTo" @change="check(order.id)" size="sm" :disabled="!payment.applyEditable"/>
                                    </td>
                                    <td>{{ order.reference_number }}</td>
                                    <td>{{ order.date | moment('MM-DD-YYYY') }}</td>
                                    <td>${{ Number(order.amount).toFixed(2) }}</td>
                                    <td><span v-if="order.type == 'Invoice'">${{ Number(order.remaining).toFixed(2) }}</span></td>
                                    <td>
                                        <span v-if="payment.applyEditable">${{ Number(order.applied).toFixed(2) }}</span>
                                        <span v-else-if="order.applied_to[payment.id]">${{ Number(order.applied_to[payment.id]).toFixed(2) }}</span>
                                        <span v-else>$0.00</span>
                                    </td>
                                </tr>
                            </b-tbody>
                        </b-table-simple>
                        </div>
                    </b-col>
                </b-row>

                <!-- <b-form-row class="button-row">
                    <b-col cols=2>
                        <b-button type="submit" variant="primary" class="btn-sm">
                            <span v-if="submitting">
                                <b-spinner small type="grow"></b-spinner>
                                Modifying Payment...
                            </span>
                            <span v-else>Modify Payment</span>
                        </b-button>
                    </b-col>
                </b-form-row> -->
            </b-form>
        </b-modal>
	</div>
</template>

<script>
    import axios from 'axios'
    import { VueAutosuggest } from "vue-autosuggest"
	export default {
		props: [
            'customerId',
            'paymentInfo'
		],

		components: {
            VueAutosuggest
		},

		data() {
			return {
				modalName: 'modal-lists-customers-edit-payment',
                payment: {
                    customerId: null,
                    depositAccount: '',
                    paymentMethod: '',
                    applyTo: [],
                    applyEditable: false,
                    amount: 0
                },
                resetted: 0,
                checkedTotal: 0,
                transactions: [],
                lists: {
                    paymentTypes: [],
                    depositAccounts: []
                },

                autosuggest: {
                    customer: {
                        suggestions: [],
                        debounceMilliseconds: 500,
                        timeout: null
                    },
                    row: {
                        suggestions: [],
                        debounceMilliseconds: 500,
                        timeout: null,
                        currentIndex: '',
                    }
                },

                keys: {
                    datepicker: 1,
                    outstanding: 1,
                    amount: 1
                },

                customerReset: 0,
                customerUpdated: 0,

                submitting: 0,
			}
		},

        methods: {
            onModalShown() {
                if (this.payment.customerId) {
                    this.getTransactions()
                }
            },

            isInvoiceRow(t) {
                return t && String(t.type || '').trim() === 'Invoice'
            },

            canChangeCustomer() {
                if ((!this.payment.applyEditable && this.transactions.length) || this.resetted) {
                    return true
                }

                return false
            },

            check(rowId) {
                let checked = this.payment.applyTo.includes(rowId)
                let index = this.findIndex(this.transactions, 'id', rowId)
                let remaining = Number(this.transactions[index].remaining)

                if (checked) {
                    if (this.available > 0) {
                        if (remaining <= this.available) {
                            this.transactions[index].applied = remaining
                            this.checkedTotal += +remaining
                        } else {
                            this.transactions[index].applied = this.available
                            this.checkedTotal += +this.available
                        }
                    } else {
                        let index = this.payment.applyTo.indexOf(rowId)
                        this.payment.applyTo.splice(index, 1)
                        this.showAlertBox('warning', 'Balance Exceeded', 'Checked orders exceeds payment amount!')
                        this.keys.outstanding++
                    }
                } else {
                    this.checkedTotal -= +this.transactions[index].applied
                    this.transactions[index].applied = 0
                }
            },

            resetApplied() {
                this.payment.applyTo.forEach((row) => {
                    let index = this.findIndex(this.transactions, 'id', row);
                    let currentRemaining = Number(this.transactions[index].remaining)
                    currentRemaining += +this.transactions[index].applied_to[this.paymentInfo.id]
                    this.transactions[index].remaining = currentRemaining.toFixed(2)
                })

                this.payment.applyTo = []
                this.payment.applyEditable = true
                this.resetted = 1
            },

            matchedPayment(rowId, ids, row) {
                if (ids) {
                    for (const key in ids) {
                        if (this.paymentInfo.id == key) {
                            this.payment.applyTo.push(rowId)
                        }
                    }
                }
            },

            clickHandlerCustomer(item) {
                let query = this.payment.customer
                if (!query) {
                    this.autosuggest.customer.suggestions = []
                }
            },

            fetchResultsCustomer() {
                const query = this.payment.customer

                clearTimeout(this.autosuggest.customer.timeout);
                this.autosuggest.customer.timeout = setTimeout(() => {
                    const customersPromise = axios.get('api/lists/customers/getcustomersfilter/' + query);

                    Promise.all([customersPromise]).then(values => {
                        this.autosuggest.customer.suggestions = []

                        const customers = values[0].data.items

                        customers.length &&
                        this.autosuggest.customer.suggestions.push({ name: "customers", data: customers })

                        if (!isNaN(query)) {
                            this.payment.customer = this.autosuggest.customer.suggestions[0].data[0].name
                            this.onSelectedCustomer({item: this.autosuggest.customer.suggestions[0].data[0]})
                        }
                    })
                }, this.autosuggest.customer.debounceMilliseconds)
            },

            onSelectedCustomer(item) {
                if (item) {
                    this.customerReset = 1

                    this.payment.customerId = item.item.id
                    this.payment.balance = item.item.balance
                    this.payment.open = item.item.open
                    this.payment.customer = item.item.name
                    this.payment.paymentType = item.item.payment

                    if (item.item.last_payment) {
                        this.payment.paymentType = item.item.last_payment.payment_types_id
                        this.payment.depositAccount = item.item.last_payment.deposit_accounts_id
                    } else {
                        this.payment.paymentType = ''
                        this.payment.depositAccount = ''
                    }

                    this.customerReset = 0

                    this.getTransactions()

                    this.keys.outstanding++

                    this.populatePaymentInfo()
                }
            },

            suggestFirstCustomer(query) {
                let suggests = this.autosuggest.customer.suggestions
                let arr = suggests[0].data

                if (arr.length) {
                    for (var i = 0; i < arr.length; i++) {
                        let cur = arr[i].name.toLowerCase()
                        if (cur.includes(query.toLowerCase())) {
                            this.customerReset = 1

                            this.payment.customer = arr[i].name
                            this.payment.customerId = arr[i].id
                            this.payment.open = arr[i].open

                            if (arr[i].last_payment) {
                                this.payment.paymentType = arr[i].last_payment.payment_types_id
                                this.payment.depositAccount = arr[i].last_payment.deposit_accounts_id
                            } else {
                                this.payment.paymentType = ''
                                this.payment.depositAccount = ''
                            }

                            this.autosuggest.customer.suggestions = []

                            this.customerReset = 0

                            this.getTransactions()

                            this.keys.outstanding++

                            break;
                        } else {
                            continue
                        }
                    }
                }
            },

            customerChanged(action) {
                if (this.customerReset == 0) {
                    this.customerUpdated = action
                }
            },

            getSuggestionValueCustomer(suggestion) {
                return suggestion.item.name
            },

            getTransactions() {
                if (!this.payment.customerId) {
                    this.transactions = []
                    return
                }
                const url = 'api/lists/customers/gettransactions/' + this.payment.customerId
                axios.get(url, {})
                    .then((res) => {
                        this.payment.applyTo = []
                        const payload = res.data || {}
                        const raw = Array.isArray(payload.transactions)
                            ? payload.transactions
                            : (payload.data && Array.isArray(payload.data.transactions) ? payload.data.transactions : [])
                        const rows = raw
                            .filter((i) => this.isInvoiceRow(i))
                            .map((i) => {
                                const row = Object.assign({}, i)
                                this.matchedPayment(row.id, row.applied_to, row)
                                if (row.applied === undefined) {
                                    row.applied = 0
                                }
                                return row
                            })
                        rows.sort((a, b) => {
                            const da = new Date(a.date).getTime()
                            const db = new Date(b.date).getTime()
                            return (isNaN(db) ? 0 : db) - (isNaN(da) ? 0 : da)
                        })
                        this.transactions = rows

                        if (!this.payment.applyTo.length) {
                            this.payment.applyEditable = true
                        } else {
                            this.payment.applyEditable = false
                        }
                        this.keys.outstanding++
                }, () => {
                    this.has_error = true
                })
            },

            editPayment() {
                this.submitting = 1
                let formData = this.payment

                let url = 'api/lists/customers/payments/editpayment'
                axios.post(url, {
                    formData,
                    transactions: this.transactions
                })
                .then((res) => {
                    this.submitting = 0

                    if (res.data.status == 'Success') {
                        this.closeModal(this.modalName)
                        this.showAlertBox('success', 'Payment Modified', 'Payment modified successfully!')
                        this.$emit('getPayments')
                        this.resetted = 0
                    } else {
                        this.showAlertBox('error', 'Error', 'Error modifying payment!')
                    }
                }, () => {
                    this.has_error = true
                })
            },

            getPaymentTypes() {
                let url = 'api/lists/customers/payments/getpaymenttypes'
                axios.get(url, {})
                .then((res) => {
                    this.lists.paymentTypes = res.data
                }, () => {
                    this.has_error = true
                })
            },

            getPaymentDepositAccounts() {
                let url = 'api/company/payment-deposit-accounts/active'
                axios.get(url, {})
                .then((res) => {
                    if (res.data.status === 'success') {
                        // Transform the data to match the expected format
                        this.lists.depositAccounts = res.data.deposit_accounts.map(account => ({
                            value: account.id,
                            text: account.name
                        }))
                        this.populatePaymentInfo()
                    }
                }, (error) => {
                    this.has_error = true
                })
            },

            populatePaymentInfo() {
                this.payment.id = this.paymentInfo.id
                this.payment.date = this.paymentInfo.date
                this.keys.datepicker++

                this.payment.amount = Math.abs(this.paymentInfo.amount)
                this.payment.paymentType = this.paymentInfo.payment_types_id
                this.payment.depositAccount = this.paymentInfo.deposit_accounts_id
                this.payment.reference = this.paymentInfo.reference
                this.payment.notes = this.paymentInfo.notes
                // this.payment.customer = this.customerId.toString()
                // this.fetchResultsCustomer()
            }
        },

        computed: {
            available() {
                let avail = this.payment.amount - this.checkedTotal
                return avail.toFixed(2)
            }
        },

        watch: {
        //     'payment.applyTo': {
        //         handler: function (after, before) {
        //             if (this.payment.applyEditable && this.payment.applyTo.length) {
        //                 let differences = after.filter(x => before.indexOf(x) === -1);
        //                 if (differences) {
        //                     let newlyChecked = differences[0]
        //                     let index = this.findIndex(this.transactions, 'id', newlyChecked);
        //                     if (index > -1) {
        //                         let remaining = Number(this.transactions[index].remaining)
        //                         if (this.available > 0) {
        //                             if (remaining <= this.available) {
        //                                 this.checkedTotal += +remaining
        //                             } else {
        //                                 this.checkedTotal += +this.available
        //                             }
        //                         } else {
        //                             let index = this.payment.applyTo.indexOf(newlyChecked)
        //                             this.payment.applyTo.splice(index, 1)
        //                             this.showAlertBox('warning', 'Balance Exceeded', 'Checked orders exceeds payment amount!');
        //                             this.keys.outstanding++
        //                         }
        //                     }
        //                 } else {

        //                 }
        //             } else {
        //                 this.checkedTotal = 0
        //             }
        //         },
        //         deep: true
        //     }
        },

        mounted() {
            this.payment.customer = this.customerId.toString()
            this.getPaymentTypes()
            this.getPaymentDepositAccounts()
            this.fetchResultsCustomer()
		}
	}
</script>
<style lang="scss">
    #modal-lists-customers-edit-payment {
        .modal-content {
            .button-row {
                margin-top: 15px;
            }
        }

        .outstanding-orders {
            border-left: 1px #ccc solid;

            h6 {
                text-align: center;
            }

            table {
                font-size: 10pt;
            }
        }

        .options-assign {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;

            input {
                width: 100%;
                padding: 0.5rem;
                height: calc(1.5em + 0.3rem + 2px);
            }

            ul {
                width: 100%;
                color: rgba(30, 39, 46,1.0);
                list-style: none;
                margin: 0;
                padding: 0.5rem 0 .5rem 0;
            }

            li {
                margin: 0 0 0 0;
                border-radius: 5px;
                padding: 0.75rem 0 0.75rem 0.75rem;
                display: flex;
                align-items: center;
            }

            li:hover {
                cursor: pointer;
            }

            .autosuggest-container {
                display: flex;
                width: 100%;

                li {
                    white-space: nowrap;
                    text-align: left;
                }
            }

            #autosuggest { width: 100%; display: block; }

            .autosuggest__results {
                position: relative;
            }

            .autosuggest__results-container {
                width: 300px;
            }

            .autosuggest__results-item--highlighted {
                background-color: rgba(51, 217, 178,0.2);
            }

            #autosuggest-autosuggest__results {
                overflow-y: hidden;
            }
        }
    }
</style>
